/* eslint-disable @typescript-eslint/no-explicit-any */
import { acceptHMRUpdate, defineStore } from 'pinia';
import { useUserSettingsStore } from '@/stores/user-settings.store';
import { AppsService, getAppIcon } from '@/common/apps/apps.service';
import {
    APP_PLATFORM_TYPES,
    getAppDisplayName,
    getAppsForPlatforms,
    getIntegratedApps,
    isWebBasedApp,
} from '@/utils/apps';
import { getAutoFeatureConfig, updateAutoFeatureConfigProperty } from '@/utils/auto-feature';
import { SdkTools } from '@pendo/services/BuildingBlocks';
import first from 'lodash/first';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import keyBy from 'lodash/keyBy';
import some from 'lodash/some';
import values from 'lodash/values';
import {
    deactivateApp,
    fetchApp,
    fetchApps,
    fetchDiscoveryApps,
    getMobileAppKey,
    updateApp,
    updateAppFlag,
    saveJwksUri,
    deleteJwksUri,
    updateAppProperty,
    updateDiscoveryAppCost,
    updateDiscoveryAppDescription,
    updateDiscoveryAppLabels,
    updateDiscoveryAppLicenses,
    updateDiscoveryAppOwner,
    updateDiscoveryAppCommandCenterItStatus,
    updateDiscoveryAppPotentialShadowIt,
    updateDiscoveryAppRenewalDate,
    updateDomains,
    fetchAppHistory,
    fetchLicenseInformationHistory,
} from '@/stores/apps.utils';

function getSubscriptionStore () {
    // Lazy to avoid the subscription -> apps -> subscription initialization cycle.
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    const { useSubscriptionStore } = require('@/stores/subscription.store');

    return useSubscriptionStore();
}

const { isMobileCodelessApp } = SdkTools;
// eslint-disable-next-line @typescript-eslint/no-require-imports
const { getDisplayName } = require('@pendo/services/Apps');
const DEFAULT_TIMEOUT = 10000;

export const useAppsStore = defineStore('apps', {
    state: () => ({
        map: {} as Record<string, any>,
        discoveryApps: [] as any[],
        discoveryAppsLoadSettled: false,
        appHistory: [] as any[],
        appHistoryFetching: false,
        appHistoryId: null as string | null,
        error: null as any,
        updatingFlag: null as string | null,
        updatingApp: false,
        autoFeatureConfig: null as any,
        deactivatedAppIdsLoaded: false,
    }),
    getters: {
        getAppById: (state) => (id: string) => state.map[id],
        getAppsByIds: (state) => (ids: string[]) => ids.map((id) => state.map[id]).filter(Boolean),
        getAppOrCategoryById: (state) => (id: string) => {
            if (id === '*' || id === 'all') return { id: 'all', displayName: 'All Apps' };
            if (id === 'web') return { id: 'web', displayName: 'Web Apps' };
            if (id === 'mobile') return { id: 'mobile', displayName: 'Mobile Apps' };
            if (id === 'extension') return { id: 'extension', displayName: 'Extension Apps' };
            if (id === 'web|extension') return { id: 'web|extension', displayName: 'Web & Extension Apps' };

            return state.map[id];
        },
        list: (state) => values(state.map).filter((app) => !AppsService.isDeactivatedApp(app)),
        discoveryAppsList: (state) => state.discoveryApps,
        deactivatedList: (state) => values(state.map).filter((app) => AppsService.isDeactivatedApp(app)),
        listAllForActiveSubscription (store: any): any[] {

            return store.list.filter((app: any) => app.subscriptionId === getSubscriptionStore().active.id);
        },
        appMapForActiveSubscription (store: any): Record<string, any> {
            return keyBy(store.listAllForActiveSubscription, 'id');
        },
        allWebPlusIntegratedMobileApps (store: any): any[] {
            return store.list.filter((app: any) => app.platform === APP_PLATFORM_TYPES.WEB || app.integrated);
        },
        onlyIntegratedApps (store: any): any[] {
            return store.list.filter((app: any) => app.integrated);
        },
        // Extension apps are guideable before data arrives, unlike other unintegrated apps.
        guidableApps (store: any): any[] {
            return store.list.filter((app: any) =>
                app.integrated || app.platform === APP_PLATFORM_TYPES.EXTENSION,
            );
        },
        webList (store: any): any[] {
            return getAppsForPlatforms(store.list, [APP_PLATFORM_TYPES.WEB]);
        },
        extensionList (store: any): any[] {
            return getAppsForPlatforms(store.list, [APP_PLATFORM_TYPES.EXTENSION]);
        },
        webLikeList (store: any): any[] {
            return getAppsForPlatforms(store.list, [APP_PLATFORM_TYPES.EXTENSION, APP_PLATFORM_TYPES.WEB]);
        },
        mobileList (store: any): any[] {
            return getAppsForPlatforms(store.list, [APP_PLATFORM_TYPES.IOS, APP_PLATFORM_TYPES.ANDROID]);
        },
        integratedWebList (store: any): any[] {
            return getIntegratedApps(store.webList);
        },
        integratedExtensionList (store: any): any[] {
            return getIntegratedApps(store.extensionList);
        },
        integratedMobileList (store: any): any[] {
            return getIntegratedApps(store.mobileList);
        },
        hasIntegratedApps (store: any): boolean {
            return store.list.some((app: any) => app.integrated === true);
        },
        retroAnalyticsList (store: any): any[] {
            return store.allWebPlusIntegratedMobileApps.filter((app: any) => {
                return isWebBasedApp(app.platform) || get(app, 'applicationFlags.mobileRetroactiveAnalyticsApp', false);
            });
        },
        hasMobileApp (store: any): any {
            const apps = store.list;
            if (apps.length && apps.length > 0) {
                return getAppsForPlatforms(apps, [APP_PLATFORM_TYPES.IOS, APP_PLATFORM_TYPES.ANDROID]);
            }

            return false;
        },
        hasV2MobileApp (store: any): boolean {
            return store.allWebPlusIntegratedMobileApps.some((app: any) => AppsService.isV2MobileApp(app));
        },
        isMultiApp (store: any): boolean {
            const apps = store.allWebPlusIntegratedMobileApps;

            return !!apps.length && apps.length > 1;
        },
        appsFromGuide (store: any) {
            return (guide: any) => {
                const appMap = store.appMapForActiveSubscription;

                return guide.appIds.map((id: string) => get(appMap, id, {}));
            };
        },
        appNameFromId (store: any) {
            return (id: string) => {
                const appMap = store.appMapForActiveSubscription;
                const app = get(appMap, id, {});

                return getDisplayName(app);
            };
        },
        getApplicationFlag (store: any) {
            return (appId: string, flag: string) => {
                const app = store.getAppById(appId);

                return get(app, `applicationFlags.${flag}`, false);
            };
        },
        isRestrictedMobileApp (store: any) {
            return (appId: string) => {
                const app = store.getAppById(appId);

                return AppsService.isMobile(app) ? !isMobileCodelessApp(app) : false;
            };
        },
        getMobileFrameworkFlavour (store: any) {
            return (appId: string) => store.getAppById(appId).mobileFrameworkFlavour;
        },
        isAppLocalizationEnabled (store: any) {
            return (appId: string) => {
                const app = store.getAppById(appId);

                return get(app, 'applicationFlags.guideLocalization', false);
            };
        },
        isAppPlatformType (store: any) {
            return (appId: string, platform: string) => {
                const app = store.getAppById(appId);

                return app?.platform === platform;
            };
        },
        hasAppOfPlatformType (store: any) {
            return (extensionPlatformType: string) => {
                if (extensionPlatformType === 'other') return false;

                return some(store.extensionList, { extensionPlatformType });
            };
        },
        appsWithSuggestedPages (store: any): any[] {
            return store.list.filter((app: any) => app.applicationFlags.suggestPageTags);
        },
        appsWithPageBasedEventProperties (store: any): any[] {
            return store.list.filter((app: any) =>
                get(app, 'applicationFlags.pageBasedEventProperties', false),
            );
        },
        guideSeenTimeoutLength (store: any): number {
            const app = first(store.webLikeList);

            return get(app, 'guideSeenTimeoutLength', DEFAULT_TIMEOUT);
        },
        getAutoFeatureConfig: (state) => (appId: string) => state.map[appId]?.autoFeatureConfig,
        locationKey (store: any) {
            return (appId: string) => {
                // Keep the guide helper behind a lazy boundary: importing it eagerly
                // loops through segment utilities and the root Vuex store while the
                // hybrid store graph is still being initialized.
                // eslint-disable-next-line @typescript-eslint/no-require-imports
                const { generateDesignerLastUrlUserSettingsKey } = require('@/guides/helpers/common.util.js');
                const { subscriptionId } = store.getAppById(appId);

                return generateDesignerLastUrlUserSettingsKey(subscriptionId, appId);
            };
        },
        adoptLocationKey (store: any) {
            return (appId: string) => {
                const { subscriptionId } = store.getAppById(appId);

                return `${subscriptionId}:${appId}--lastDesignerUrl`;
            };
        },
        lastKnownAppUrls (store: any): Record<string, any> {
            return store.list.reduce((urlMap: Record<string, any>, app: any) => {
                const { id } = app;
                urlMap[id] =
                    useUserSettingsStore().settingByName(store.locationKey(id))?.value ||
                    useUserSettingsStore().settingByName(store.adoptLocationKey(id))?.value;

                return urlMap;
            }, {});
        },
        getEndUserMessaging (store: any) {
            return (appId: string) =>
                (store.getApplicationFlag(appId, 'adoptAnalyticsForwarding')
                    ? 'Reseller Partners'
                    : 'Adopt end users');
        },
        analyticsAppsListWithIcons (store: any): any[] | null {
            if (Object.keys(store.map).length === 0) return null;

            return AppsService.sortApps(
                store.list
                    .filter((el: any) => el.integrated && !el.isDeleted)
                    .map((app: any) =>
                        Object.freeze({
                            id: app.id,
                            kind: 'anyActivity',
                            platform: app.platform,
                            isDeleted: !!app.isDeleted,
                            integrated: !!app.integrated,
                            firstEventTime: app.firstEventTime,
                            iconUrl: app.iconUrl,
                            icon: getAppIcon(app),
                            codeName: app.name,
                            name: app.displayName,
                            displayName: app.displayName,
                            applicationFlags: app.applicationFlags,
                            ...(app.recordingStartingEventTime && {
                                recordingStartingEventTime: app.recordingStartingEventTime,
                            }),
                            ...(app.trainingAttributes && { trainingAttributes: app.trainingAttributes }),
                        }),
                    ),
            );
        },
        getIconByAppId (store: any) {
            return (appId: string) => {
                const app = store.getAppById(appId);

                return getAppIcon(app);
            };
        },
        getAppsWithoutVisitorType (store: any): any[] {
            return store.list.filter((app: any) => !app.visitorType);
        },
        firstAvailableApp (store: any): any {
            const allApps = store.analyticsAppsListWithIcons;
            if (!allApps || allApps.length === 0) return null;

            const firstItem = allApps[0];
            if (firstItem?.apps && firstItem.apps.length > 0) return firstItem.apps[0];

            return firstItem;
        },
    },
    actions: {
        setApp ({ app }: { app: any }) {
            this.map[app.id] = app;
        },
        setAgentProductionVersion ({ appId, version }: { appId: string; version: string }) {
            const appConfig = this.map[appId];
            if (appConfig?.agentVersionProd) {
                appConfig.agentVersionProd = version;
            }
        },
        async fetchDiscoveryApps () {
            try {
                const discoveryApps = await fetchDiscoveryApps();
                this.discoveryApps = discoveryApps;

                return discoveryApps;
            } catch (error) {
                this.error = error;
                throw error;
            } finally {
                this.discoveryAppsLoadSettled = true;
            }
        },
        async updateDiscoveryAppLicenses ({ discoveryAppId, licenses }: any) {
            return updateDiscoveryAppLicenses(discoveryAppId, licenses);
        },
        async updateDiscoveryAppCost ({ appId, cost }: any) {
            return updateDiscoveryAppCost(appId, cost);
        },
        async updateDiscoveryAppOwner ({ appId, ownedByUserId }: any) {
            return updateDiscoveryAppOwner(appId, ownedByUserId);
        },
        async updateDiscoveryAppRenewalDate ({ appId, renewalDate }: any) {
            const result = await updateDiscoveryAppRenewalDate(appId, renewalDate);
            const index = this.discoveryApps.findIndex((a) => a.id === appId);
            if (index !== -1) {
                this.discoveryApps = this.discoveryApps.map((app) => {
                    if (app.id === appId) {
                        return { ...app, renewalDate };
                    }

                    return app;
                });
            }

            return result;
        },
        async updateDiscoveryAppPotentialShadowIt ({ discoveryAppId, potentialShadowIt }: any) {
            return updateDiscoveryAppPotentialShadowIt(discoveryAppId, potentialShadowIt);
        },
        async updateDiscoveryAppCommandCenterItStatus ({ discoveryAppId, commandCenterItStatus }: any) {
            return updateDiscoveryAppCommandCenterItStatus(discoveryAppId, commandCenterItStatus);
        },
        async hydrate ({ applications }: { applications?: any[] } = {}) {
            if (!applications) {
                await this.loadAll({ reFetch: true });

                return;
            }
            this.map = keyBy(applications, 'id');
        },
        async loadAll ({ reFetch = false, includeDeactivated }: { reFetch?: boolean; includeDeactivated?: boolean } = {}) {
            const needsDeactivated = includeDeactivated && !this.deactivatedAppIdsLoaded;
            if (!isEmpty(this.map) && !reFetch && !needsDeactivated) return true;

            try {
                const applications = await fetchApps({
                    includeDeactivated: includeDeactivated || this.deactivatedAppIdsLoaded,
                });

                const { isAdoptEndUser } = getSubscriptionStore();
                if (isAdoptEndUser) {
                    applications.forEach((app: any) => {
                        app.displayName = getAppDisplayName(app);
                    });
                }

                this.map = keyBy(applications, 'id');
                if (includeDeactivated) this.deactivatedAppIdsLoaded = true;

                return true;
            } catch (error) {
                this.error = error;

                return false;
            }
        },
        async loadOne ({ reFetch = false, id }: { reFetch?: boolean; id: string } = {} as any) {
            if (this.map[id] && !reFetch) return;

            try {
                const app = await fetchApp(id);

                const { isAdoptEndUser } = getSubscriptionStore();
                if (isAdoptEndUser) app.displayName = getAppDisplayName(app);

                this.map[app.id] = app;
            } catch (error) {
                this.error = error;
            }
        },
        async deactivateApp ({ id }: { id: string }) {
            try {
                await deactivateApp(id);
            } catch (error) {
                this.error = error;
                throw error;
            }
        },
        async updateApp ({ app, updates }: { app: any; updates: any }) {
            const existingApp = this.map[app.id]
                ?? this.map[String(app.id)]
                ?? this.map[Number(app.id)];
            try {
                this.updatingApp = true;
                const updatedApp = await updateApp({
                    ...existingApp,
                    id: existingApp?.id ?? app.id,
                    ...updates,
                });

                const { isAdoptEndUser } = getSubscriptionStore();
                if (isAdoptEndUser) updatedApp.displayName = getAppDisplayName(updatedApp);

                this.map[updatedApp.id] = updatedApp;

                if (AppsService.isMobile(updatedApp)) {
                    await this.getMobileAppKey({ appId: updatedApp.id });
                }

                this.updatingApp = false;
            } catch (error) {
                this.updatingApp = false;
                this.error = error;
                throw error;
            }
        },
        async updateDomains ({ app, urls }: { app: any; urls: any }) {
            await updateDomains(app.id, urls);
        },
        async updateFlag ({ appId, flag, enabled }: { appId: string; flag: string; enabled: boolean }) {
            this.updatingFlag = flag;
            try {
                await updateAppFlag(appId, flag, enabled);
                this.map[appId].applicationFlags[flag] = enabled;
                this.updatingFlag = null;
            } catch (error) {
                this.error = error;
                this.updatingFlag = null;
                throw error;
            }
        },
        async saveJwksUri ({ appId, jwksUri }: { appId: string; jwksUri: string }) {
            try {
                this.error = null;
                const app = await saveJwksUri(appId, jwksUri);
                this.map[app.id] = app;

                return app;
            } catch (error) {
                this.error = error;
                throw error;
            }
        },
        async deleteJwksUri ({ appId }: { appId: string }) {
            try {
                this.error = null;
                const app = await deleteJwksUri(appId);
                this.map[app.id] = app;

                return app;
            } catch (error) {
                this.error = error;
                throw error;
            }
        },
        async getMobileAppKey ({ appId }: { appId: string }) {
            try {
                this.error = null;
                const { mobileAppKey } = await getMobileAppKey(appId);
                this.map[appId].mobileAppKey = mobileAppKey;
            } catch (error) {
                this.error = error;
            }
        },
        async updateAppProperty ({ propertyName, propertyValue, id }: { propertyName: string; propertyValue: any; id: string }) {
            try {
                this.error = null;
                const updatedAppProperty = await updateAppProperty(id, propertyName, propertyValue);
                this.map[id][propertyName] = updatedAppProperty;
            } catch (error) {
                this.error = error;
                throw error;
            }
        },
        async updateAttributesOnMultipleApps ({ apps, updates }: { apps: any[]; updates: any }) {
            try {
                this.updatingApp = true;
                await Promise.all(apps.map((app) => this.updateApp({ app, updates })));
                this.updatingApp = false;
            } catch (error) {
                this.updatingApp = false;
                throw error;
            }
        },
        async fetchAutoFeatureConfig (appId: string) {
            const autoFeatureConfig = await getAutoFeatureConfig(appId);
            this.map[appId].autoFeatureConfig = autoFeatureConfig;
        },
        async updateAutoFeatureConfig ({ appId, property, value }: { appId: string; property: string; value: any }) {
            const testValue = this.map[appId]?.autoFeatureConfig[property];
            const autoFeatureConfig = await updateAutoFeatureConfigProperty(
                appId,
                property,
                testValue?.length === 0 ? null : testValue,
                value,
            );
            this.map[appId].autoFeatureConfig = autoFeatureConfig;
        },
        resetAppHistory ({ appId }: { appId: string }) {
            this.appHistoryId = appId;
            this.appHistory = [];
        },
        async fetchAppHistory ({
            appId,
            licenseInformationId,
        }: {
            appId: string;
            licenseInformationId?: string | null;
        }) {
            this.appHistoryId = appId;
            this.appHistory = [];
            this.appHistoryFetching = true;
            try {
                const history = licenseInformationId
                    ? await fetchLicenseInformationHistory(licenseInformationId)
                    : await fetchAppHistory(appId);
                if (appId === this.appHistoryId) this.appHistory = history;
            } finally {
                this.appHistoryFetching = false;
            }
        },
        async updateDiscoveryAppDescription ({ appId, description }: { appId: string; description: string }) {
            return updateDiscoveryAppDescription(appId, description);
        },
        async updateDiscoveryAppLabels ({ appId, labels }: { appId: string; labels: any }) {
            const result = await updateDiscoveryAppLabels(appId, labels);
            const index = this.discoveryApps.findIndex((a) => a.id === appId);
            if (index !== -1) {
                this.discoveryApps = this.discoveryApps.map((app) => {
                    if (app.id === appId) {
                        return { ...app, labels };
                    }

                    return app;
                });
            }

            return result;
        },
    },
});

if (module.hot) {
    module.hot.accept(acceptHMRUpdate(useAppsStore, module.hot));
}
